import { useInfiniteQuery } from '@tanstack/react-query';

import { DEFAULT_PAGE_SIZE_NOTIFICATIONS } from '@/lib/constants/pagination';

import { getNotifications, NotificationsPaginatedResponse } from '../api/notifications.api';
import { Notification } from '../types';

interface UseNotificationsParams {
  /** Filter by read status. undefined = all, false = unread only */
  isRead?: boolean;
  /** Enable or disable the query */
  enabled?: boolean;
}

interface UseNotificationsResult {
  notifications: Notification[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: Error | null;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  totalCount: number;
}

/**
 * Hook for fetching paginated notifications with infinite scroll support.
 */
export const useGetNotifications = ({
  isRead,
  enabled = true,
}: UseNotificationsParams = {}): UseNotificationsResult => {
  const { data, isLoading, isFetchingNextPage, error, hasNextPage, fetchNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ['notifications', { isRead }],
      queryFn: async ({ pageParam = 1 }) => {
        return getNotifications({
          page: pageParam,
          pageSize: DEFAULT_PAGE_SIZE_NOTIFICATIONS,
          isRead,
        });
      },
      getNextPageParam: (lastPage: NotificationsPaginatedResponse, allPages) => {
        const loadedCount = allPages.reduce((acc, page) => acc + page.items.length, 0);
        const hasMore = loadedCount < lastPage.pagination.total;
        if (!hasMore) return undefined;
        return (lastPage.pagination.page ?? 0) + 1;
      },
      initialPageParam: 1,
      enabled,
    });

  const notifications = data?.pages.flatMap(page => page.items) ?? [];
  const totalCount = data?.pages[0]?.pagination.total ?? 0;

  return {
    notifications,
    isLoading,
    isFetchingNextPage,
    error: error as Error | null,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    refetch,
    totalCount,
  };
};
